import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionContext, readPersistedSuperAdmin } from "@/hooks/useSessionContext";
import { toast } from "sonner";

/**
 * Check if current user is a super_admin. Reads from the session-context
 * bootstrap (single round trip on login, cached for entire session). Falls
 * back to a persisted localStorage hint while the bootstrap is still in
 * flight so super admins never see a misleading "loading" / subscription
 * gate. RLS still enforces the real permission server-side.
 */
export function useIsSuperAdmin() {
  const { user } = useAuth();
  const { data, isLoading, isFetching, isError, error } = useSessionContext();
  const persistedHint = user?.id ? readPersistedSuperAdmin(user.id) : false;
  const resolved = data?.isSuperAdmin;
  const eagerSuperAdmin = resolved === true || (resolved === undefined && persistedHint);
  return {
    data: resolved !== undefined ? resolved : (isLoading ? persistedHint || undefined : false),
    isLoading: eagerSuperAdmin ? false : isLoading,
    isFetching,
    isError,
    error,
  } as const;
}

/**
 * Fetch all organizations (only accessible to super_admins via RLS).
 */
export function useOrganizations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["platform-organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

/**
 * Fetch org member counts for display.
 */
export function useOrgMemberCounts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["platform-org-member-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_members")
        .select("organization_id");
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((m) => {
        counts[m.organization_id] = (counts[m.organization_id] || 0) + 1;
      });
      return counts;
    },
    enabled: !!user,
  });
}

/**
 * Fetch tenant health metrics per org.
 */
export function useTenantHealthMetrics(orgId?: string) {
  return useQuery({
    queryKey: ["platform-health", orgId],
    queryFn: async () => {
      const filters = orgId ? { organization_id: orgId } : {};

      const [profiles, invoices, expenses, auditLogs] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).match(filters),
        supabase.from("invoices").select("id", { count: "exact", head: true }).match(filters),
        supabase.from("expenses").select("id", { count: "exact", head: true }).match(filters),
        supabase.from("audit_logs").select("id", { count: "exact", head: true }).match(filters),
      ]);

      return {
        activeUsers: profiles.count ?? 0,
        invoiceCount: invoices.count ?? 0,
        expenseCount: expenses.count ?? 0,
        auditVolume: auditLogs.count ?? 0,
      };
    },
    enabled: true,
  });
}

/**
 * Fetch platform admin logs (superadmin audit trail).
 */
export function usePlatformAdminLogs() {
  return useQuery({
    queryKey: ["platform-admin-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_admin_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Log a superadmin action.
 */
export function useLogPlatformAction() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      action: string;
      target_type: string;
      target_id?: string;
      target_name?: string;
      metadata?: Record<string, unknown>;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase.from("platform_admin_logs").insert([{
        admin_id: user.id,
        action: params.action,
        target_type: params.target_type,
        target_id: params.target_id,
        target_name: params.target_name,
        metadata: (params.metadata ?? {}) as any,
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-admin-logs"] });
    },
  });
}

/**
 * Suspend or reactivate an organization.
 * Validates status transitions and prevents self-suspension.
 */
export function useOrgStatusAction() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const logAction = useLogPlatformAction();

  return useMutation({
    mutationFn: async (params: { orgId: string; orgName: string; newStatus: "active" | "suspended" }) => {
      if (!user) throw new Error("Not authenticated");
      if (!["active", "suspended"].includes(params.newStatus)) {
        throw new Error("Invalid status. Must be 'active' or 'suspended'.");
      }

      // Verify current status to prevent redundant operations
      const { data: org } = await supabase
        .from("organizations")
        .select("status")
        .eq("id", params.orgId)
        .single();
      if (!org) throw new Error("Organization not found");
      if (org.status === params.newStatus) {
        throw new Error(`Organization is already ${params.newStatus}`);
      }

      const { error } = await supabase
        .from("organizations")
        .update({ status: params.newStatus })
        .eq("id", params.orgId);
      if (error) throw error;

      await logAction.mutateAsync({
        action: params.newStatus === "suspended" ? "org_suspended" : "org_reactivated",
        target_type: "organization",
        target_id: params.orgId,
        target_name: params.orgName,
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["platform-organizations"] });
      toast.success(`Organization ${vars.newStatus === "suspended" ? "suspended" : "reactivated"}`);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
